import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useForm,
  Controller,
  useFieldArray,
  useWatch,
  Control,
  FieldArrayWithId,
  UseFieldArrayReturn,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  Select,
  MenuItem,
  FormHelperText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Chip,
  Card,
  CardContent,
  Switch,
  Divider,
  FormControlLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from "@mui/material";
import {
  NumericEntryField,
  NumericDisplayField,
} from "../../components/common/CustomFields";
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Inventory as InventoryIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  Print as PrintIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  useTSQComplaint,
  useTSMCreateComplaint,
  useTSMPostProcessComplaint,
  useTSMApproveComplaint,
  useTSMUpdateComplaint,
} from "../../hooks/useTSQualityRecords";
import {
  ComplaintFull,
  ComplaintCreate,
  ComplaintCategoryType,
  COMPLAINT_CATEGORIES,
  ComplaintProductData,
  ComplaintUpdate,
} from "../../types/qualityRecordTypes";
import { DynamicRequestSchema } from "../../types/dynamicQueryTypes";
import { useTSQDynamicList } from "../../hooks/useTSDynamicQuery";
import {
  TypedSelectOption,
  YupTypedSelectOption,
} from "../../types/common/selectOptions";
import stringUtils from "../../utils/stringUtils";
import "../../styles/chipOverrides";
import { useAuth } from "../../contexts/AuthContextWithQuery";
import { AddrData, IdAsUUID } from "../../types/common/commonTypes";
import { useTSQAddressList } from "../../hooks/useTSAddresses";
import useFormParams, { BASE_FORM_MODES } from "../../hooks/useFormHooks";
import { FormTextField } from "../../components/common/FormTextField";
import { ViewIcon } from "../../components/common/NucleusSymbolsAndIcons";

const COMPLAINT_FORM_MODES = [...BASE_FORM_MODES];
type ComplaintFormMode = (typeof COMPLAINT_FORM_MODES)[number];

interface ComplaintFormParams {
  id: string | null;
}

type CFCategoryOption = TypedSelectOption<
  "cf_category",
  {
    require_other: boolean;
    summary_prefill: string | null;
  },
  ComplaintCategoryType
>;

const CFCategoryOptions: CFCategoryOption[] = [
  {
    label: "Alarm - Air Leak",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Air Leak Alarm",
    },
  },
  {
    label: "Alarm - Canister Disconnected",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Canister Disconnected Alarm",
    },
  },
  {
    label: "Alarm - Canister Full",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Canister Full Alarm",
    },
  },
  {
    label: "Alarm - Clogged Filter",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Clogged Filter Alarm",
    },
  },
  {
    label: "Alarm - Drainage Obstruction",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Drainage Obstruction Alarm",
    },
  },
  {
    label: "Alarm - Error",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Alarm - Smartcheck",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Smartcheck Alarm",
    },
  },
  {
    label: "Alarm - Tipped Over",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Tipped Over Alarm",
    },
  },
  {
    label: "Alarm - Watchdog",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: "Watchdog Alarm",
    },
  },
  {
    label: "Alarm - Other",
    type: "cf_category",
    value: {
      id: "ALARM",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Chest Tube - Other",
    type: "cf_category",
    value: {
      id: "CHEST_TUBE",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Control Module - Drainage Volume",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Drainage volume innaccurate",
    },
  },
  {
    label: "Control Module - Failed System Check",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Failed System Check",
    },
  },
  {
    label: "Control Module - Fault",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Fault",
    },
  },
  {
    label: "Control Module - Not Charging",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Not Charging",
    },
  },
  {
    label: "Control Module - Not Powering On",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Not Powering On",
    },
  },
  {
    label: "Control Module - Requires Service",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Requires Service",
    },
  },
  {
    label: "Control Module - Requires Software/Firmware Update",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Requires Software/Firmware Update",
    },
  },
  {
    label: "Control Module - Suction Issue",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module Suction Issue",
    },
  },
  {
    label: "Control Module - System Freeze",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: "Control Module System Freezing",
    },
  },
  {
    label: "Control Module - Other",
    type: "cf_category",
    value: {
      id: "CONTROL_MODULE",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Damage - Feet",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: "Damaged/Broken Feet",
    },
  },
  {
    label: "Damage - Handle",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: "Damaged/Broken Handle",
    },
  },
  {
    label: "Damage - Latch",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: "Damaged/Broken Latch",
    },
  },
  {
    label: "Damage - Power Port",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: "Damaged/Broken Power Port",
    },
  },
  {
    label: "Damage - Screen",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: "Damaged/Broken Screen",
    },
  },
  {
    label: "Damage - Other",
    type: "cf_category",
    value: {
      id: "DAMAGE",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Drainage Kit - Other",
    type: "cf_category",
    value: {
      id: "DRAINAGE_KIT",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "Power Supply - Other",
    type: "cf_category",
    value: {
      id: "POWER_SUPPLY",
      require_other: false,
      summary_prefill: null,
    },
  },
  {
    label: "OTHER",
    type: "cf_category",
    value: {
      id: "OTHER",
      require_other: true,
      summary_prefill: null,
    },
  },
];
type ReportedToOption = TypedSelectOption<
  "rep_to",
  {
    first: string;
    last: string;
    email: string;
  }
>;
type ItemOption = TypedSelectOption<"item", {}>;
type LotOption = TypedSelectOption<"lot", {}>;

type CustomerOption = TypedSelectOption<
  "customer",
  { name: string; code: string }
>;
type AddressOption = TypedSelectOption<
  "address",
  {
    attn: string | null;
    addressee: string | null;
    line_1: string;
    line_2: string | null;
    line_3: string | null;
    city: string;
    state: string;
    country: string;
    zip: string;
  }
>;

interface ProductDataEntry {
  lot: string;
  id: IdAsUUID;
  quantity: number;
  returning: boolean;
}

interface DialogProdDataEntry extends ProductDataEntry {
  item: string;
}

interface ProductData {
  TGCM: ProductDataEntry[];
  TGDK: ProductDataEntry[];
  TGCT20: ProductDataEntry[];
  TGCT28: ProductDataEntry[];
  TGPS: ProductDataEntry[];
}

interface CFFormData {
  cf_category: CFCategoryOption | null;
  date_of_occurrence: Date;
  cat_other: string;
  issue_summary: string;
  nature_of_issue: string;
  customer: CustomerOption | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  rma_addr: AddrData | null;
  date_reported: Date;
  reported_to: ReportedToOption | null;
  product_data: ProductData;
}
const YupProductData = yup.object({
  lot: yup.string().required("Lot Number is required"),
  id: yup.string().required("ID is Required"),
  quantity: yup.number().min(1, "At least 1 is required").required(),
  returning: yup.boolean().required(),
});

// Validation schema
const complaintSchema = yup.object({
  date_of_occurrence: yup.date().required("Date of Occurrence is Required"),
  cf_category: YupTypedSelectOption.test({
    name: "category",
    test: (value, context) => {
      const formMode = context.options.context?.formMode || null;
      if (formMode === "create" && !value) {
        return context.createError({
          path: "cf_category",
          message: "CF Category is Required",
        });
      }
      return true;
    },
  }),
  cat_other: yup.string().test({
    name: "cat-other",
    test: (value, context) => {
      const formMode = context.options.context?.formMode || null;
      const catType: CFCategoryOption | null =
        context.parent.cf_category || null;
      if (
        formMode === "create" ||
        (formMode === "edit" && catType?.value.require_other)
      ) {
        if (!catType) {
          return context.createError({
            path: "cat_other",
            message: "Category must be selected.",
          });
        } else {
          if (catType.value.require_other && !value) {
            return context.createError({
              path: "cat_other",
              message: "Currently Selected Category Requires Context",
            });
          }
        }
      }
      return true;
    },
  }),
  issue_summary: yup.string().required("Issue Summary is Required"),
  nature_of_issue: yup.string().required("Nature of Issue is Required"),
  contact_name: yup
    .string()
    .optional()
    .test({
      name: "contact-name",
      test: (value, context) => {
        if (context.options.context?.anyReturning && !value) {
          return context.createError({
            path: "contact_name",
            message:
              "RMA Contact Name is Required when any product is returning.",
          });
        }
        return true;
      },
    }),
  contact_email: yup
    .string()
    .optional()
    .test({
      name: "contact-email",
      test: (value, context) => {
        if (context.parent.contact_name) {
          if (!value && !context.parent.contact_phone) {
            return context.createError({
              path: "contact_email",
              message:
                "Either the phone number or email is required if Contact is present.",
            });
          }
        }
        return true;
      },
    }),
  contact_phone: yup
    .string()
    .optional()
    .test({
      name: "contact-phone",
      test: (value, context) => {
        if (context.parent.contact_name) {
          if (!value && !context.parent.contact_email) {
            return context.createError({
              path: "contact_phone",
              message:
                "Either the phone number or email is required if Contact is present.",
            });
          }
        }
        return true;
      },
    }),
  customer: YupTypedSelectOption.required("Customer is Required"),
  rma_addr: yup
    .object({
      attn: yup
        .string()
        .nullable()
        .test({
          name: "addr-attn",
          test: (value, context) => {
            if (context.parent) {
              const addressee = context.parent.addressee;
              if (!value && !addressee) {
                return context.createError({
                  path: "rma_addr.attn",
                  message:
                    "Either the Attn or Addressee lines need to be filled.",
                });
              }
            }
            return true;
          },
        }),
      addressee: yup
        .string()
        .nullable()
        .test({
          name: "addr-addressee",
          test: (value, context) => {
            if (context.parent) {
              const attn = context.parent.attn;
              if (!value && !attn) {
                return context.createError({
                  path: "rma_addr.addressee",
                  message:
                    "Either the Attn or Addressee lines need to be filled.",
                });
              }
            }
            return true;
          },
        }),
      line_1: yup.string().required("Address line 1 is Required"),
      line_2: yup.string().nullable(),
      line_3: yup.string().nullable(),
      city: yup.string().required("City is Required"),
      state: yup.string().required("State is Required"),
      country: yup.string().required("Country is Required"),
      zip: yup.string().required("Zip is Required"),
    })
    .nullable()
    .test({
      name: "rma-addr",
      test: (value, context) => {
        if (
          context.options.context?.haveAddr &&
          context.options.context?.anyReturning
        ) {
          if (!value) {
            return context.createError({
              path: "rma_addr",
              message:
                'RMA Address must be included if the "Have Address" toggle is "Yes"',
            });
          }
        }
        return true;
      },
    }),
  date_reported: yup.date().required("Date Reported is Required"),
  reported_to: YupTypedSelectOption.required("Reported To is Required"),
  product_data: yup
    .object({
      TGCM: yup.array(YupProductData).required(),
      TGDK: yup.array(YupProductData).required(),
      TGCT20: yup.array(YupProductData).required(),
      TGCT28: yup.array(YupProductData).required(),
      TGPS: yup.array(YupProductData).required(),
    })
    .test({
      name: "product-data",
      test: (value, context) => {
        if (
          value.TGCM.length +
            value.TGDK.length +
            value.TGCT20.length +
            value.TGCT28.length +
            value.TGPS.length ===
          0
        ) {
          return context.createError({
            path: "product_data",
            message: "At least one product must be provided",
          });
        }
        if (!value) {
          return context.createError({
            path: "product_data",
            message: "At least one product must be provided",
          });
        }
        return true;
      },
    }),
});

const AddProductDialog: React.FC<{
  open: boolean;
  itemOpts: ItemOption[];
  allLots: string[];
  onCancel: () => void;
  onClose: (
    event: {},
    reason: "backdropClick" | "escapeKeyDown" | null,
  ) => void;
  onSubmit: (data: DialogProdDataEntry[]) => void;
}> = ({ open, itemOpts, allLots, onCancel, onClose, onSubmit }) => {
  const [curData, setCurData] = useState<DialogProdDataEntry[]>([]);

  const [curItem, setCurItem] = useState<ItemOption | null>(null);
  const [curItemErr, setCurItemErr] = useState<string | null>(null);
  const [curLot, setCurLot] = useState<LotOption | null>(null);
  const [curLotErr, setCurLotErr] = useState<string | null>(null);
  const [curQuant, setCurQuant] = useState<number>(1);
  const [curQuantErr, setCurQuantErr] = useState<string | null>(null);
  const [curReturning, setCurReturning] = useState<boolean>(false);

  useEffect(() => {
    setCurData([]);
    setCurItem(null);
    setCurLot(null);
    setCurQuant(1);
    setCurReturning(false);
  }, [open]);

  const {
    data: lotsResponse,
    isLoading: lotsLoading,
    isFetching: lotsFetching,
    error: lotsError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "inventory_lots",
      primary_fields: ["id", "lot_number"],
      alt_name: "lot",
      filters: [
        [
          {
            table: "inventory_lots",
            alt_name: "lot",
            field: "item_id",
            operator: "==",
            value: curItem?.value.id,
            case_insensitive: false,
            inclusive_end: false,
            inclusive_start: false,
            is_not: false,
          },
        ],
      ],
      sorts: [
        {
          table: "inventory_lots",
          alt_name: "lot",
          field: "lot_number",
          asc: false,
        },
      ],
      joins: [],
      subqueries: [],
    },
    open && !!curItem,
  );
  const lotOptions: LotOption[] = useMemo(() => {
    if (lotsResponse?.items && !!curItem) {
      const lots: LotOption[] = lotsResponse.items.map((val) => ({
        label: val["lot.lot_number"],
        type: "lot",
        value: {
          id: val["lot.id"],
        },
      }));
      lots.unshift({
        label: "UNKNOWN",
        type: "lot",
        value: {
          id: "UNKNOWN",
        },
      });
      return lots;
    } else {
      return [
        {
          label: "UNKNOWN",
          type: "lot",
          value: {
            id: "UNKNOWN",
          },
        },
      ];
    }
  }, [lotsResponse, curItem]);

  const addSelection = () => {
    if (!curItem) {
      setCurItemErr("Must have an Item Selected");
      return;
    } else {
      setCurItemErr(null);
    }
    if (!curLot) {
      setCurLotErr("Must select an option");
      return;
    } else {
      setCurLotErr(null);
    }
    if (!curQuant || curQuant < 1) {
      setCurQuantErr("Quantity must be greater than 1");
      return;
    } else {
      setCurQuantErr(null);
    }
    setCurData((prev) => [
      ...prev,
      {
        lot: curLot.label,
        id: curLot.value.id,
        quantity: curQuant,
        returning: curReturning,
        item: curItem!.label,
      },
    ]);
    setCurLot(null);
    setCurQuant(1);
  };
  return (
    <Dialog
      open={open}
      onClose={(e, r) => onClose(e, r)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{`Add New Product`}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={1} p={3}>
          <Box display="flex" gap={0.5}>
            <Autocomplete
              value={curItem}
              options={itemOpts}
              onChange={(e, v) => setCurItem(v)}
              fullWidth
              getOptionLabel={(opt) => opt?.label || ""}
              isOptionEqualToValue={(opt, val) => opt.value.id === val.value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Item"
                  error={!!curItemErr}
                  helperText={curItemErr}
                />
              )}
            />
            <Autocomplete
              value={curLot}
              options={lotOptions}
              loading={lotsLoading || lotsFetching || !curItem}
              loadingText={
                !curItem ? "Select an Item to load lots..." : "Loading lots..."
              }
              onChange={(e, v) => setCurLot(v)}
              fullWidth
              getOptionLabel={(opt) => opt?.label || ""}
              getOptionDisabled={(opt) =>
                opt.label !== "UNKNOWN" && allLots.includes(opt.label)
              }
              isOptionEqualToValue={(opt, val) => opt.value.id === val.value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Lot"
                  error={!!curLotErr}
                  helperText={curLotErr}
                />
              )}
            />
          </Box>
          <Box display="flex" gap={1}>
            <NumericEntryField
              value={curQuant}
              decimalPlaces={0}
              isCurrency={false}
              onChange={(v) => setCurQuant(v)}
              textFieldProps={{ sx: { width: "50%" } }}
            />
            <Box display="flex" width="50%">
              <FormControlLabel
                control={
                  <Switch
                    value={curReturning}
                    onChange={(e, c) => setCurReturning(c)}
                    size="small"
                  />
                }
                label="Returning"
                labelPlacement="top"
              />
            </Box>
          </Box>
          <Button
            variant="contained"
            color="info"
            onClick={addSelection}
            disabled={!curItem || !curLot || !curQuant}
          >
            {!curItem
              ? "Select Item"
              : !curLot
                ? "Select Lot"
                : !curQuant
                  ? "Add Quantity"
                  : "Add Lot"}
          </Button>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="30%">Item</TableCell>
                <TableCell width="30%">Lot</TableCell>
                <TableCell width="20%">Quantity</TableCell>
                <TableCell width="20%">Returning</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {curData.map((v, i) => (
                <TableRow key={`cur_data_${i}`}>
                  <TableCell>{v.item}</TableCell>
                  <TableCell>{v.lot}</TableCell>
                  <TableCell>{v.quantity}</TableCell>
                  <TableCell>{v.returning ? "Yes" : "No"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant={"outlined"} color="error">
          Cancel
        </Button>
        <Button
          onClick={(e) => onSubmit(curData)}
          variant={"contained"}
          color="success"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ProductDataTable: React.FC<{
  tgcm: UseFieldArrayReturn<CFFormData, "product_data.TGCM", "id">;
  tgdk: UseFieldArrayReturn<CFFormData, "product_data.TGDK", "id">;
  tgct20: UseFieldArrayReturn<CFFormData, "product_data.TGCT20", "id">;
  tgct28: UseFieldArrayReturn<CFFormData, "product_data.TGCT28", "id">;
  tgps: UseFieldArrayReturn<CFFormData, "product_data.TGPS", "id">;
  triggerDialogOpen: () => void;
  formMode: ComplaintFormMode;
}> = ({ tgcm, tgdk, tgct20, tgct28, tgps, triggerDialogOpen, formMode }) => {
  return (
    <Stack direction="column" gap={1} width="100%">
      {formMode !== "view" && (
        <Box display="flex" maxWidth="200px">
          <Button
            variant="outlined"
            color="info"
            onClick={() => triggerDialogOpen()}
          >
            Add Product
          </Button>
        </Box>
      )}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "10%" }}>Row #</TableCell>
              <TableCell sx={{ width: "40%" }}>Lot/SN</TableCell>
              <TableCell sx={{ width: "20%" }}>Quantity</TableCell>
              <TableCell sx={{ width: "20%" }}>Returning</TableCell>
              <TableCell sx={{ width: "10%" }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="h4">TGCM1000</Typography>
              </TableCell>
            </TableRow>
            {tgcm.fields.length > 0 ? (
              <>
                {tgcm.fields.map((field, ind) => {
                  const item = field as unknown as ProductDataEntry & {
                    id: string;
                  };
                  return (
                    <TableRow key={field.id}>
                      <TableCell>TGCM #{ind + 1}</TableCell>
                      <TableCell>{item.lot}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.returning ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {formMode !== "view" && (
                          <Tooltip title="Remove Row">
                            <IconButton
                              size="small"
                              onClick={() => tgdk.remove(ind)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1">
                    No TGCM1000 configured for this Complaint Request
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="h4">TGDK110012</Typography>
              </TableCell>
            </TableRow>
            {tgdk.fields.length > 0 ? (
              <>
                {tgdk.fields.map((field, ind) => {
                  const item = field as unknown as ProductDataEntry & {
                    id: string;
                  };
                  return (
                    <TableRow key={field.id}>
                      <TableCell>TGDK #{ind + 1}</TableCell>
                      <TableCell>{item.lot}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.returning ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {formMode !== "view" && (
                          <Tooltip title="Remove Row">
                            <IconButton
                              size="small"
                              onClick={() => tgdk.remove(ind)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1">
                    No TGDK110012 configured for this Complaint Request
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="h4">TGCT120020</Typography>
              </TableCell>
            </TableRow>
            {tgct20.fields.length > 0 ? (
              <>
                {tgct20.fields.map((field, ind) => {
                  const item = field as unknown as ProductDataEntry & {
                    id: string;
                  };
                  return (
                    <TableRow key={field.id}>
                      <TableCell>TGCT20 #{ind + 1}</TableCell>
                      <TableCell>{item.lot}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.returning ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {formMode !== "view" && (
                          <Tooltip title="Remove Row">
                            <IconButton
                              size="small"
                              onClick={() => tgct20.remove(ind)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1">
                    No TGCT120020 configured for this Complaint Request
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="h4">TGCT120028</Typography>
              </TableCell>
            </TableRow>
            {tgct28.fields.length > 0 ? (
              <>
                {tgct28.fields.map((field, ind) => {
                  const item = field as unknown as ProductDataEntry & {
                    id: string;
                  };
                  return (
                    <TableRow key={field.id}>
                      <TableCell>TGCT28 #{ind + 1}</TableCell>
                      <TableCell>{item.lot}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.returning ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {formMode !== "view" && (
                          <Tooltip title="Remove Row">
                            <IconButton
                              size="small"
                              onClick={() => tgct28.remove(ind)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1">
                    No TGCT120028 configured for this Complaint Request
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} align="center">
                <Typography variant="h4">TGPS0100</Typography>
              </TableCell>
            </TableRow>
            {tgps.fields.length > 0 ? (
              <>
                {tgps.fields.map((field, ind) => {
                  const item = field as unknown as ProductDataEntry & {
                    id: string;
                  };
                  return (
                    <TableRow key={field.id}>
                      <TableCell>TGPS #{ind + 1}</TableCell>
                      <TableCell>{item.lot}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>{item.returning ? "Yes" : "No"}</TableCell>
                      <TableCell>
                        {formMode !== "view" && (
                          <Tooltip title="Remove Row">
                            <IconButton
                              size="small"
                              onClick={() => tgps.remove(ind)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body1">
                    No TGPS0100 configured for this Complaint Request
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell colSpan={5}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

export const PageComplaintForm: React.FC = () => {
  const location = useLocation();

  // Mode detection from URL query parameters
  // const searchParams = new URLSearchParams(location.search);
  // const id = searchParams.get("id");
  // const formMode = searchParams.get("mode") || "";
  const { mode: formMode, id } = useFormParams<
    ComplaintFormMode,
    ComplaintFormParams
  >(COMPLAINT_FORM_MODES, ["id"], "view");
  const refFormMode = useRef(formMode);
  console.log({ formMode, id });
  const [resetForm, setResetForm] = useState<boolean>(formMode !== "create");

  const { user } = useAuth();

  const today = new Date();
  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);
  today.setMilliseconds(0);
  const tomorrow = (() => {
    let temp = new Date(today);
    temp.setDate(temp.getDate() + 1);
    return temp;
  })();

  const formDefaults: CFFormData = {
    date_of_occurrence: today,
    cf_category: null,
    cat_other: "",
    issue_summary: "",
    nature_of_issue: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    customer: null,
    rma_addr: null,
    date_reported: today,
    reported_to: null,
    product_data: {
      TGCM: [],
      TGDK: [],
      TGCT20: [],
      TGCT28: [],
      TGPS: [],
    },
  };

  const [anyReturning, setAnyReturning] = useState<boolean>(false);
  const [haveAddr, setHaveAddr] = useState<boolean>(true);
  // Form setup
  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isSubmitting },
    reset,
    watch,
    setValue,
    getValues,
  } = useForm<CFFormData>({
    resolver: yupResolver(complaintSchema) as any,
    context: {
      anyReturning: anyReturning,
      formMode: formMode,
      haveAddr: haveAddr,
    },
    defaultValues: formDefaults,
  });

  const refRenderCount = useRef<number>(0);
  const [displayProcessDialog, setDisplayProcessDialog] =
    useState<boolean>(false);
  const [processDialogError, setProcessDialogError] = useState<string | null>(
    null,
  );
  const handleProcessDialogClose = (
    event: any,
    reason: "backdropClick" | "escapeKeyDown",
    navTo?: "view" | "list",
    navToId?: string,
  ) => {
    if (reason === "backdropClick") {
      return;
    }
    setDisplayProcessDialog(false);
    if (navTo === "view" && navToId) {
      window.open(
        `/quality/customer_complaint/form?mode=view&id=${navToId}`,
        "_self",
      );
    } else if (navTo === "list") {
      window.open("/quality/customer_complaint", "_self");
    }
  };

  // Form State
  const [error, setError] = useState<string | null>(null);

  const [selectedCustId, setSelectedCustId] = useState<string | null>(null);
  const [selectedAddr, setSelectedAddr] = useState<AddressOption | null>(null);

  const createCFMutation = useTSMCreateComplaint();
  const updateCFMutation = useTSMUpdateComplaint();
  const approveCFMutation = useTSMApproveComplaint();
  const postProcCFMutation = useTSMPostProcessComplaint();

  const approveComplaint = async () => {
    if (!id) {
      setError(
        "Missing Complaint ID, Cannot Approve. Refresh the page and try again. Notify IT if this issue persists.",
      );
      return;
    }
    setDisplayProcessDialog(true);
    try {
      const appResult = await approveCFMutation.mutateAsync({ id: id });
      if (appResult.success) {
        const procResult = await postProcCFMutation.mutateAsync({ id: id });
        if (!procResult.success) {
          setProcessDialogError(
            `Complaint Post Processing failed: ${procResult.message || "UNKNOWN REASON"}`,
          );
        } else {
        }
      } else {
        setProcessDialogError(
          `Complaint Approval failed: ${appResult.message || "UNKNOWN REASON"}`,
        );
      }
    } catch (e) {
      setError(
        "There was an error completing the Complaint Approval Process. Notify IT so it can be fixed. If you want to be extra helpful, you can send a screenshot of the browser console.",
      );
      console.error(e);
      return;
    }
  };

  const {
    data: thisCFData,
    isLoading: thisCFLoading,
    isFetching: thisCFFetching,
    error: thisCFError,
  } = useTSQComplaint(id, formMode === "view" || formMode === "edit");

  const {
    data: emailResponse,
    isLoading: emailLoading,
    isFetching: emailFetching,
    error: emailError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "employees",
      alt_name: "emp",
      primary_fields: ["id", "email"],
      joins: [],
      filters: [
        [
          {
            table: "employees",
            alt_name: "emp",
            field: "id",
            operator: "==",
            value: [thisCFData?.submitted_by, thisCFData?.reported_to],
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [],
      subqueries: [],
    },
    formMode !== "create" && !!thisCFData,
  );

  const matchEmails: string[] = useMemo(() => {
    if (emailResponse?.items) {
      return emailResponse.items.map((val) => val["emp.email"]);
    }
    return [];
  }, [emailResponse]);

  const {
    data: customerResponse,
    isLoading: customerLoading,
    isFetching: customerFetching,
    error: customerError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "customers",
      alt_name: "cust",
      primary_fields: ["id", "code", "name"],
      joins: [],
      filters: [],
      sorts: [
        {
          table: "customers",
          alt_name: "cust",
          field: "code",
          asc: true,
        },
      ],
      subqueries: [],
    },
    true,
  );
  const custOpts: CustomerOption[] = useMemo(() => {
    if (customerResponse?.items) {
      return customerResponse.items.map((val) => ({
        label: `${val["cust.code"]} - ${val["cust.name"]}`,
        type: "customer",
        value: {
          id: val["cust.id"],
          code: val["cust.code"],
          name: val["cust.name"],
        },
      }));
    } else {
      return [];
    }
  }, [customerResponse]);

  const {
    data: repToResponse,
    isLoading: repToLoading,
    isFetching: repToFetching,
    error: repToError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "employees",
      alt_name: "emp",
      primary_fields: ["id", "first_name", "last_name", "email"],
      joins: [],
      filters: [
        [
          {
            table: "employees",
            alt_name: "emp",
            field: "clinical",
            operator: "==",
            value: true,
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [
        {
          table: "employees",
          alt_name: "emp",
          field: "first_name",
          asc: true,
        },
      ],
      subqueries: [],
    },
    true,
  );
  const repToOpts: ReportedToOption[] = useMemo(() => {
    if (repToResponse?.items) {
      const reps: ReportedToOption[] = repToResponse.items.map((val) => ({
        label: `${val["emp.first_name"]} ${val["emp.last_name"]}`,
        type: "rep_to",
        value: {
          id: val["emp.id"],
          first: val["emp.first_name"],
          last: val["emp.last_name"],
          email: val["emp.email"],
        },
      }));
      if (!!user && refFormMode.current === "create") {
        for (let rep of reps) {
          if (rep.value.email === user.email) {
            setValue("reported_to", rep);
            break;
          }
        }
      }
      return reps;
    } else {
      return [];
    }
  }, [repToResponse, user, setValue]);

  const baseDataLoading =
    customerFetching || customerLoading || repToLoading || repToFetching;

  const {
    data: addrResponse,
    isLoading: addrLoading,
    isFetching: addrFetching,
    error: addrError,
  } = useTSQAddressList(
    {
      skip: -1,
      limit: -1,
      active_status: "active",
      filters: [
        [
          {
            field: "entity_id",
            operator: "==",
            value: selectedCustId,
          },
        ],
      ],
    },
    !!selectedCustId,
  );
  const allAddrOpts: AddressOption[] = useMemo(() => {
    if (addrResponse?.items) {
      const addrs: AddressOption[] = addrResponse.items.map((val) => ({
        label: `${val.line_1} ${val.city_obj.name}, ${val.state_obj.code} ${val.country_obj.code} ${val.zip}`,
        type: "address",
        value: {
          id: val.id,
          attn: val.attn || null,
          addressee: val.addressee || null,
          line_1: val.line_1,
          line_2: val.line_2 || null,
          line_3: val.line_3 || null,
          city: val.city_obj.name,
          state: val.state_obj.name,
          country: val.country_obj.name,
          zip: val.zip,
        },
      }));
      return addrs;
    } else {
      return [];
    }
  }, [addrResponse]);
  const addrReady = !addrLoading && !addrFetching && allAddrOpts.length > 0;

  const {
    data: itemsResponse,
    isLoading: itemsLoading,
    isFetching: itemsFetching,
    error: itemsError,
  } = useTSQDynamicList(
    {
      page: 0,
      page_size: 1000,
      primary: "items",
      alt_name: "item",
      primary_fields: ["id", "number"],
      filters: [
        [
          {
            table: "items",
            alt_name: "item",
            field: "number",
            operator: "==",
            value: ["FGN-1387", "FGS-1125", "FGS-1126", "FGS-2000", "MSP-2020"],
            is_not: false,
            inclusive_end: false,
            inclusive_start: false,
            case_insensitive: false,
          },
        ],
      ],
      sorts: [],
      joins: [],
      subqueries: [],
    },
    formMode !== "view",
  );

  const itemOpts: ItemOption[] = useMemo(() => {
    if (itemsResponse?.items) {
      let tgcmOpt: ItemOption | null = null;
      let tgdkOpt: ItemOption | null = null;
      let tgct20Opt: ItemOption | null = null;
      let tgct28Opt: ItemOption | null = null;
      let tgpsOpt: ItemOption | null = null;
      for (let item of itemsResponse.items) {
        switch (item["item.number"]) {
          case "FGN-1387":
            tgcmOpt = {
              label: "TGCM1000",
              type: "item",
              value: {
                id: item["item.id"],
              },
            };
            break;

          case "FGS-1125":
            tgdkOpt = {
              label: "TGDK110012",
              type: "item",
              value: {
                id: item["item.id"],
              },
            };
            break;

          case "FGS-1126":
            tgct20Opt = {
              label: "TGCT120020",
              type: "item",
              value: {
                id: item["item.id"],
              },
            };
            break;

          case "FGS-2000":
            tgct28Opt = {
              label: "TGCT120028",
              type: "item",
              value: {
                id: item["item.id"],
              },
            };
            break;

          case "MSP-2020":
            tgpsOpt = {
              label: "TGPS0100",
              type: "item",
              value: {
                id: item["item.id"],
              },
            };
            break;

          default:
            continue;
        }
      }
      if (!tgcmOpt || !tgdkOpt || !tgct20Opt || !tgct28Opt || !tgpsOpt) {
        setError(
          "Unable to locate item details for one of the Catalog Goods. Refresh the page and notify IT if this issue persists.",
        );
        return [];
      }
      return [tgcmOpt, tgdkOpt, tgct20Opt, tgct28Opt, tgpsOpt];
    }
    return [];
  }, [itemsResponse]);

  const dataReady =
    !baseDataLoading && custOpts.length > 0 && repToOpts.length > 0;

  const [prodDialogOpen, setProdDialogOpen] = useState<boolean>(false);

  const handleProdDialogOpen = () => {
    setProdDialogOpen(true);
  };

  const handleProdDialogCancel = () => {
    console.log("Prod Dialog Cancelled");
    handleProdDialogClose({}, null);
  };

  const handleProdDialogClose = (
    event: {},
    reason: "backdropClick" | "escapeKeyDown" | null,
  ) => {
    if (reason === "backdropClick") {
      console.log("Prod Dialog backdropClick");
      return;
    }
    console.log("Prod Dialog Closing");
    setProdDialogOpen(false);
  };

  const handleProdDialogSubmit = (data: DialogProdDataEntry[]) => {
    console.log("Prod Dialog Submitted", data);
    const [
      tgcmEntries,
      tgdkEntries,
      tgct20Entries,
      tgct28Entries,
      tgpsEntries,
    ]: [
      ProductDataEntry[],
      ProductDataEntry[],
      ProductDataEntry[],
      ProductDataEntry[],
      ProductDataEntry[],
    ] = (() => {
      const tgcmEntries: ProductDataEntry[] = [];
      const tgdkEntries: ProductDataEntry[] = [];
      const tgct20Entries: ProductDataEntry[] = [];
      const tgct28Entries: ProductDataEntry[] = [];
      const tgpsEntries: ProductDataEntry[] = [];
      for (let prod of data) {
        switch (prod.item) {
          case "":
            setError(
              "Could not determine which Product Category to assign this to, assignment failed. Notify IT if this issue persists.",
            );
            break;
          case "TGCM1000":
            tgcmEntries.push({
              lot: prod.lot,
              id: prod.id,
              quantity: prod.quantity,
              returning: prod.returning,
            });
            break;
          case "TGDK110012":
            tgdkEntries.push({
              lot: prod.lot,
              id: prod.id,
              quantity: prod.quantity,
              returning: prod.returning,
            });
            break;
          case "TGCT120020":
            tgct20Entries.push({
              lot: prod.lot,
              id: prod.id,
              quantity: prod.quantity,
              returning: prod.returning,
            });
            break;
          case "TGCT120028":
            tgct28Entries.push({
              lot: prod.lot,
              id: prod.id,
              quantity: prod.quantity,
              returning: prod.returning,
            });
            break;
          case "TGPS0100":
            tgpsEntries.push({
              lot: prod.lot,
              id: prod.id,
              quantity: prod.quantity,
              returning: prod.returning,
            });
            break;
        }
      }
      return [
        tgcmEntries,
        tgdkEntries,
        tgct20Entries,
        tgct28Entries,
        tgpsEntries,
      ];
    })();
    tgcmFieldArray.append(tgcmEntries);
    tgdkFieldArray.append(tgdkEntries);
    tgct20FieldArray.append(tgct20Entries);
    tgct28FieldArray.append(tgct28Entries);
    tgpsFieldArray.append(tgpsEntries);
    handleProdDialogClose({}, null);
  };

  const tgcmFieldArray = useFieldArray({ control, name: "product_data.TGCM" });
  const [returningTGCM, tgcmLots]: [ProductDataEntry[], string[]] =
    useMemo(() => {
      const returningTGCM: ProductDataEntry[] = [];
      const tgcmLots: string[] = [];
      tgcmFieldArray.fields.forEach((v) => {
        if (v.returning) {
          returningTGCM.push(v);
        }
        tgcmLots.push(v.lot);
      });
      return [returningTGCM, tgcmLots];
    }, [tgcmFieldArray.fields]);

  const tgdkFieldArray = useFieldArray({ control, name: "product_data.TGDK" });
  const [returningTGDK, tgdkLots]: [ProductDataEntry[], string[]] =
    useMemo(() => {
      const returningTGDK: ProductDataEntry[] = [];
      const tgdkLots: string[] = [];
      tgdkFieldArray.fields.forEach((v) => {
        if (v.returning) {
          returningTGDK.push(v);
        }
        tgdkLots.push(v.lot);
      });
      return [returningTGDK, tgdkLots];
    }, [tgdkFieldArray.fields]);

  const tgct20FieldArray = useFieldArray({
    control,
    name: "product_data.TGCT20",
  });
  const [returningTGCT20, tgct20Lots]: [ProductDataEntry[], string[]] =
    useMemo(() => {
      const returningTGCT20: ProductDataEntry[] = [];
      const tgct20Lots: string[] = [];
      tgct20FieldArray.fields.forEach((v) => {
        if (v.returning) {
          returningTGCT20.push(v);
        }
        tgct20Lots.push(v.lot);
      });
      return [returningTGCT20, tgct20Lots];
    }, [tgct20FieldArray.fields]);

  const tgct28FieldArray = useFieldArray({
    control,
    name: "product_data.TGCT28",
  });
  const [returningTGCT28, tgct28Lots]: [ProductDataEntry[], string[]] =
    useMemo(() => {
      const returningTGCT28: ProductDataEntry[] = [];
      const tgct28Lots: string[] = [];
      tgct28FieldArray.fields.forEach((v) => {
        if (v.returning) {
          returningTGCT28.push(v);
        }
        tgct28Lots.push(v.lot);
      });
      return [returningTGCT28, tgct28Lots];
    }, [tgct28FieldArray.fields]);

  const tgpsFieldArray = useFieldArray({ control, name: "product_data.TGPS" });
  const [returningTGPS, tgpsLots]: [ProductDataEntry[], string[]] =
    useMemo(() => {
      const returningTGPS: ProductDataEntry[] = [];
      const tgpsLots: string[] = [];
      tgpsFieldArray.fields.forEach((v) => {
        if (v.returning) {
          returningTGPS.push(v);
        }
        tgpsLots.push(v.lot);
      });
      return [returningTGPS, tgpsLots];
    }, [tgpsFieldArray.fields]);

  const allLots: string[] = useMemo(() => {
    return [
      ...tgcmLots,
      ...tgdkLots,
      ...tgct20Lots,
      ...tgct28Lots,
      ...tgpsLots,
    ];
  }, [tgcmLots, tgdkLots, tgct20Lots, tgct28Lots, tgpsLots]);

  useEffect(() => {
    if (resetForm) {
      if (thisCFData && dataReady) {
        const editProdData: ProductData = {
          TGCM: thisCFData.product_data.TGCM.map((val) => ({
            lot: val[0],
            id: val[1] || "UNKNOWN",
            quantity: val[2],
            returning: val[3],
          })),
          TGDK: thisCFData.product_data.TGDK.map((val) => ({
            lot: val[0],
            id: val[1] || "UNKNOWN",
            quantity: val[2],
            returning: val[3],
          })),
          TGCT20: thisCFData.product_data.TGCT20.map((val) => ({
            lot: val[0],
            id: val[1] || "UNKNOWN",
            quantity: val[2],
            returning: val[3],
          })),
          TGCT28: thisCFData.product_data.TGCT28.map((val) => ({
            lot: val[0],
            id: val[1] || "UNKNOWN",
            quantity: val[2],
            returning: val[3],
          })),
          TGPS: thisCFData.product_data.TGPS.map((val) => ({
            lot: val[0],
            id: val[1] || "UNKNOWN",
            quantity: val[2],
            returning: val[3],
          })),
        };
        const origData: CFFormData = {
          date_of_occurrence: new Date(thisCFData.date_of_occurrence),
          cf_category:
            CFCategoryOptions.find(
              (opt) =>
                opt.value.id === thisCFData.cf_category &&
                opt.label.toLowerCase().includes("other"),
            ) || null,
          cat_other: thisCFData.cat_other || "",
          issue_summary: thisCFData.issue_summary,
          nature_of_issue: thisCFData.nature_of_issue,
          contact_name: thisCFData.contact_name || "",
          contact_email: thisCFData.contact_email || "",
          contact_phone: thisCFData.contact_phone || "",
          customer:
            custOpts.find((opt) => opt.value.id === thisCFData.customer_id) ||
            null,
          rma_addr: thisCFData.rma_addr,
          date_reported: new Date(thisCFData.date_reported),
          reported_to:
            repToOpts.find((opt) => opt.value.id === thisCFData.reported_to) ||
            null,
          product_data: editProdData,
        };
        setHaveAddr(!!thisCFData.rma_addr);
        reset(origData);
        setSelectedCustId(origData.customer?.value.id || null);
        setResetForm(false);
      }
    }
  }, [resetForm, thisCFData, dataReady, repToOpts, custOpts, reset]);

  const [watchedRMAAddr] = watch(["rma_addr"]);

  useEffect(() => {
    const count =
      returningTGCM.length +
      returningTGDK.length +
      returningTGCT20.length +
      returningTGCT28.length +
      returningTGPS.length;
    setAnyReturning(count > 0);
    if (refFormMode.current === "create") {
      if (count > 0 && haveAddr && !watchedRMAAddr) {
        console.log("CREATING RMA ADDR OBJECT");
        setValue("rma_addr", {
          addressee: null,
          attn: null,
          line_1: "",
          line_2: null,
          line_3: null,
          city: "",
          state: "",
          country: "",
          zip: "",
        });
      } else if (watchedRMAAddr && (!haveAddr || count <= 0)) {
        console.log("SETTING RMA ADDR TO NULL");
        setValue("rma_addr", null);
      }
    } else if (refFormMode.current === "edit") {
      if (!watchedRMAAddr) {
        if (count > 0 && haveAddr) {
          console.log("CREATING RMA ADDR OBJECT");
          setValue("rma_addr", {
            addressee: null,
            attn: null,
            line_1: "",
            line_2: null,
            line_3: null,
            city: "",
            state: "",
            country: "",
            zip: "",
          });
        }
      } else {
        if (!haveAddr && !watchedRMAAddr.attn && !watchedRMAAddr.addressee) {
          console.log("SETTING EMPTY ADDR OBJECT NULL");
          setValue("rma_addr", null);
        }
      }
    }
  }, [
    returningTGCM,
    returningTGDK,
    returningTGCT20,
    returningTGCT28,
    returningTGPS,
    haveAddr,
    watchedRMAAddr,
    setValue,
  ]);

  useEffect(() => {
    console.log("Render #", refRenderCount.current);
    console.log("Base Data Ready", dataReady);
    console.log("Form Mode", formMode);
    console.log("Ref Form Mode", refFormMode.current);
  });

  useEffect(() => {
    refRenderCount.current += 1;
  });

  const switchToViewMode = () => {
    window.open(
      `/quality/customer_complaint/form?mode=view&id=${thisCFData!.id}`,
      "_self",
    );
  };

  const switchToEditMode = () => {
    window.open(
      `/quality/customer_complaint/form?mode=edit&id=${thisCFData!.id}`,
      "_self",
    );
  };

  const switchToCreateMode = () => {
    window.open(`/quality/customer_complaint/form?mode=create`, "_self");
  };

  // Form submission
  const onSubmit = async (data: CFFormData) => {
    try {
      setDisplayProcessDialog(true);
      setProcessDialogError(null);
      setError(null);
      if (!user) {
        setError(
          "Unable to determine Current User, cannot submit form. Please refresh the page. Notify IT if this problem persists.",
        );
        return;
      }
      if (formMode === "create") {
        const prodData: ComplaintProductData = {
          TGCM: data.product_data.TGCM.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGDK: data.product_data.TGDK.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGCT20: data.product_data.TGCT20.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGCT28: data.product_data.TGCT28.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGPS: data.product_data.TGPS.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
        };
        const cfLabel: string = (() => {
          const cust: string = data.customer!.value.name;
          const reason: string = data.issue_summary;
          const item: string = (() => {
            if (data.product_data.TGCM.length > 0) {
              return "Thoraguard Control Module";
            } else if (data.product_data.TGDK.length > 0) {
              return "Thoraguard Drainage Kit";
            } else if (data.product_data.TGCT20.length > 0) {
              return "Thoraguard Chest Tube Kit, 20 Fr";
            } else if (data.product_data.TGCT28.length > 0) {
              return "Thoraguard Chest Tube Kit, 28 Fr";
            } else if (data.product_data.TGPS.length > 0) {
              return "Thoraguard Power Supply";
            } else {
              return "UNKNOWN PRODUCT";
            }
          })();
          return `${item}, ${cust}, ${reason}`;
        })();
        const cfPayload: ComplaintCreate = {
          cf_label: cfLabel,
          date_of_occurrence: data.date_of_occurrence.toISOString(),
          cf_category: data.cf_category!.value.id,
          cat_other: data.cat_other,
          issue_summary: data.issue_summary,
          nature_of_issue: data.nature_of_issue,
          contact_name: data.contact_name !== "" ? data.contact_name : null,
          contact_email: data.contact_email !== "" ? data.contact_email : null,
          contact_phone: data.contact_phone !== "" ? data.contact_phone : null,
          customer_id: data.customer!.value.id,
          rma_addr:
            anyReturning && haveAddr
              ? {
                  attn: data.rma_addr!.attn,
                  addressee: data.rma_addr!.addressee,
                  line_1: data.rma_addr!.line_1,
                  line_2: data.rma_addr!.line_2,
                  line_3: data.rma_addr!.line_3,
                  city: data.rma_addr!.city,
                  state: data.rma_addr!.state,
                  country: data.rma_addr!.country,
                  zip: data.rma_addr!.zip,
                }
              : null,
          date_reported: data.date_reported.toISOString(),
          reported_to: data.reported_to!.value.id,
          product_data: prodData,
          submitted_by: user!.user_id,
        };
        console.log("SUBMITTED COMPLAINT", cfPayload);
        const new_cf = await createCFMutation.mutateAsync(cfPayload);
        console.log("COMPLAINT SUCCESSFULLY CREATED");
      } else if (formMode === "edit") {
        if (!thisCFData) {
          setError(
            "Unable to locate original data, cannot submit Updates. Refresh the page and try again. Notify IT if this issue persists.",
          );
          return;
        }
        const prodData: ComplaintProductData = {
          TGCM: data.product_data.TGCM.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGDK: data.product_data.TGDK.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGCT20: data.product_data.TGCT20.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGCT28: data.product_data.TGCT28.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
          TGPS: data.product_data.TGPS.map((val) => [
            val.lot,
            val.id === "UNKNOWN" ? null : val.id,
            val.quantity,
            val.returning,
          ]),
        };
        const cfLabel: string = (() => {
          const cust: string = data.customer!.value.name;
          const reason: string = data.issue_summary;
          const item: string = (() => {
            if (data.product_data.TGCM.length > 0) {
              return "Thoraguard Control Module";
            } else if (data.product_data.TGDK.length > 0) {
              return "Thoraguard Drainage Kit";
            } else if (data.product_data.TGCT20.length > 0) {
              return "Thoraguard Chest Tube Kit, 20 Fr";
            } else if (data.product_data.TGCT28.length > 0) {
              return "Thoraguard Chest Tube Kit, 28 Fr";
            } else if (data.product_data.TGPS.length > 0) {
              return "Thoraguard Power Supply";
            } else {
              return "UNKNOWN PRODUCT";
            }
          })();
          return `${item}, ${cust}, ${reason}`;
        })();
        const cfPayload: ComplaintUpdate = {
          cf_label: cfLabel !== thisCFData.cf_label ? cfLabel : undefined,
          date_of_occurrence:
            data.date_of_occurrence.toISOString() !==
            thisCFData.date_of_occurrence
              ? data.date_of_occurrence.toISOString()
              : undefined,
          cf_category:
            data.cf_category!.value.id !== thisCFData.cf_category
              ? data.cf_category!.value.id
              : undefined,
          cat_other:
            data.cat_other !== thisCFData.cat_other
              ? data.cat_other
              : undefined,
          issue_summary:
            data.issue_summary !== thisCFData.issue_summary
              ? data.issue_summary
              : undefined,
          nature_of_issue:
            data.nature_of_issue !== thisCFData.nature_of_issue
              ? data.nature_of_issue
              : undefined,
          contact_name:
            (data.contact_name !== "" ? data.contact_name : null) !==
            thisCFData.contact_name
              ? data.contact_name !== ""
                ? data.contact_name
                : null
              : undefined,
          contact_email:
            (data.contact_email !== "" ? data.contact_email : null) !==
            thisCFData.contact_email
              ? data.contact_email !== ""
                ? data.contact_email
                : null
              : undefined,
          contact_phone:
            (data.contact_phone !== "" ? data.contact_phone : null) !==
            thisCFData.contact_phone
              ? data.contact_phone !== ""
                ? data.contact_phone
                : null
              : undefined,
          customer_id:
            data.customer!.value.id !== thisCFData.customer_id
              ? data.customer!.value.id
              : undefined,
          rma_addr:
            (anyReturning && haveAddr
              ? {
                  attn: data.rma_addr!.attn,
                  addressee: data.rma_addr!.addressee,
                  line_1: data.rma_addr!.line_1,
                  line_2: data.rma_addr!.line_2,
                  line_3: data.rma_addr!.line_3,
                  city: data.rma_addr!.city,
                  state: data.rma_addr!.state,
                  country: data.rma_addr!.country,
                  zip: data.rma_addr!.zip,
                }
              : null) !== thisCFData.rma_addr
              ? anyReturning && haveAddr
                ? {
                    attn: data.rma_addr!.attn,
                    addressee: data.rma_addr!.addressee,
                    line_1: data.rma_addr!.line_1,
                    line_2: data.rma_addr!.line_2,
                    line_3: data.rma_addr!.line_3,
                    city: data.rma_addr!.city,
                    state: data.rma_addr!.state,
                    country: data.rma_addr!.country,
                    zip: data.rma_addr!.zip,
                  }
                : null
              : undefined,
          date_reported:
            data.date_reported.toISOString() !== thisCFData.date_reported
              ? data.date_reported.toISOString()
              : undefined,
          reported_to:
            data.reported_to!.value.id !== thisCFData.reported_to
              ? data.reported_to!.value.id
              : undefined,
          product_data:
            prodData !== thisCFData.product_data ? prodData : undefined,
        };
        console.log("SUBMITTED COMPLAINT UPDATE", cfPayload);
        const results = await updateCFMutation.mutateAsync({
          id: id || "",
          data: cfPayload,
        });
        if (results.ok) {
          console.log("COMPLAINT SUCCESSFULLY UPDATED", results);
        }
      } else {
        return;
      }
    } catch (err: any) {
      console.error("Failed to save cf:", err);
      setError(
        err.response?.data?.detail || err.message || "Failed to save cf",
      );
    }
  };

  const doPostProc = async () => {
    if (!id) {
      console.log("NO RECORD ID");
      return;
    }
    const res = await postProcCFMutation.mutateAsync({ id: id });
    console.log("Post Proc Results", res);
  };

  const handleCancel = () => {
    window.open("/quality/customer_complaint", "_self");
  };

  if (resetForm) {
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleCancel}
            variant="outlined"
          >
            Back to Complaint List
          </Button>
          <Typography variant="h4" component="h1">
            {formMode === "create"
              ? "Complaint Initiation"
              : formMode === "view"
                ? `View Complaint Request ${thisCFData?.gl_cf_num || `NUC-CF-${thisCFData?.cf_number.toString().padStart(4, "0")}` || "Unkown CF"}`
                : formMode === "edit"
                  ? `Edit Complaint Request ${thisCFData?.gl_cf_num || `NUC-CF-${thisCFData?.cf_number.toString().padStart(4, "0")}` || "Unkown CF"}`
                  : "Uh-Oh"}
          </Typography>
          {formMode === "view" && (
            <Button
              variant="outlined"
              onClick={switchToCreateMode}
              startIcon={<AddIcon />}
            >
              Create New
            </Button>
          )}
          {formMode !== "create" && !!thisCFData && (
            <Button
              variant="outlined"
              onClick={() => {
                if (formMode === "edit") {
                  switchToViewMode();
                } else {
                  switchToEditMode();
                }
              }}
              startIcon={formMode === "edit" ? <EditIcon /> : <ViewIcon />}
            >
              {formMode === "edit" ? "View" : "Edit"}
            </Button>
          )}
          {formMode !== "create" &&
            !thisCFData?.approved &&
            (user?.is_superuser || matchEmails.includes(user?.email || "")) && (
              <Button
                variant="outlined"
                onClick={() => {
                  approveComplaint();
                }}
              >
                Approve Complaint
              </Button>
            )}
          {user?.is_superuser && (
            <Button
              variant="outlined"
              onClick={() => {
                console.log({
                  formValues: getValues(),
                  haveAddr: haveAddr,
                  repOpts: repToOpts,
                  custOpts: custOpts,
                  addrOpts: allAddrOpts,
                  thisData: thisCFData,
                  thisUser: user,
                  anyReturning: anyReturning,
                });
              }}
            >
              DEBUG PRINT
            </Button>
          )}
          {user?.is_superuser && id && (
            <Button
              variant="outlined"
              onClick={() => {
                doPostProc();
              }}
            >
              DO POST PROCESS
            </Button>
          )}
        </Box>

        {/* Error Alert */}
        {(error || thisCFError || customerError || repToError || addrError) && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error ||
              thisCFError?.message ||
              customerError?.message ||
              repToError?.message ||
              addrError?.message ||
              "An unknown error occurred"}
          </Alert>
        )}

        {formMode === "view" ? (
          <>
            {!!thisCFData && !(thisCFLoading || thisCFFetching) && dataReady ? (
              <Paper sx={{ p: 3 }}>
                <Stack spacing={3} width="100%">
                  <Stack spacing={3} sx={{ width: "100%" }}>
                    <Stack direction="column" spacing={1} width="100%">
                      <Typography variant="h3">Issue Details</Typography>
                      <Box display="flex" gap={1} width="100%">
                        <Stack direction="column" spacing={1} width="50%">
                          <FormTextField
                            mode={formMode}
                            label="Issue Category (Form Text)"
                            value={thisCFData.cf_category}
                            fullWidth
                            disabled
                          />
                          {/* <TextField
                            label="Issue Category (Custom Override)"
                            value={thisCFData.cf_category}
                            fullWidth
                            disabled
                            sx={{
                              "& .MuiInputBase-input.Mui-disabled": {
                                WebkitTextFillColor: "rgb(0,0,0)", // restore text color
                                opacity: 1,
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "rgba(0, 0, 0, 0.23)", // restore normal border color
                                },
                              "& .MuiInputLabel-root.Mui-disabled": {
                                WebkitTextFillColor: "rgb(0,0,0)", // restore label text color
                                opacity: 1,
                              },
                            }}
                          /> */}
                          <TextField
                            label="Issue Summary"
                            value={thisCFData.issue_summary}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label="Date of Occurrence"
                            value={new Date(
                              thisCFData.date_of_occurrence,
                            ).toLocaleDateString("en-US")}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Stack>
                        <Stack direction="column" spacing={1} width="50%">
                          <TextField
                            label="Category: Other - Details"
                            value={thisCFData.cat_other || ""}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label="Nature of Issue"
                            value={thisCFData.nature_of_issue}
                            rows={4}
                            multiline={true}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                    <Stack direction="column" spacing={1} width="100%">
                      <Typography variant="h3">
                        Customer/Contact Details
                      </Typography>
                      <Box display="flex" gap={1} width={"100%"}>
                        <Stack direction="column" spacing={1} width="50%">
                          <TextField
                            label="Customer"
                            value={
                              custOpts.find(
                                (v) => v.value.id === thisCFData.customer_id,
                              )?.label || "UNKNOWN CUSTOMER"
                            }
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label="Reported To"
                            value={
                              repToOpts.find(
                                (v) => v.value.id === thisCFData.reported_to,
                              )?.label || "UNKNOWN REPORTED TO"
                            }
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label="Date Reported"
                            value={new Date(
                              thisCFData.date_reported,
                            ).toLocaleDateString("en-US")}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Stack>
                        <Stack direction="column" spacing={1} width="50%">
                          <TextField
                            label={
                              !!thisCFData.rma_addr
                                ? "RMA Contact Name"
                                : "Contact Name"
                            }
                            value={thisCFData.contact_name}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label={
                              !!thisCFData.rma_addr
                                ? "RMA Contact Email"
                                : "Contact Email"
                            }
                            value={thisCFData.contact_email}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                          <TextField
                            label={
                              !!thisCFData.rma_addr
                                ? "RMA Contact Phone"
                                : "Contact Phone"
                            }
                            value={thisCFData.contact_phone}
                            fullWidth
                            slotProps={{
                              input: {
                                readOnly: true,
                              },
                            }}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                    <Box display="flex" gap={1} width="100%">
                      <Stack direction="column" spacing={1} width="65%">
                        <Typography variant="h3">Product Details</Typography>
                        <ProductDataTable
                          tgcm={tgcmFieldArray}
                          tgdk={tgdkFieldArray}
                          tgct20={tgct20FieldArray}
                          tgct28={tgct28FieldArray}
                          tgps={tgpsFieldArray}
                          triggerDialogOpen={handleProdDialogOpen}
                          formMode={formMode}
                        />
                      </Stack>
                      <Stack direction="column" spacing={1} width="35%">
                        {!!thisCFData.rma_addr && (
                          <>
                            <Typography variant="h3">Return Address</Typography>
                            <TextField
                              label="Attention"
                              value={thisCFData.rma_addr.attn || ""}
                              fullWidth
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                            />
                            <TextField
                              label="Addressee"
                              value={thisCFData.rma_addr.addressee || ""}
                              fullWidth
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                            />
                            <TextField
                              label="Line 1"
                              value={thisCFData.rma_addr.line_1}
                              fullWidth
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                            />
                            <TextField
                              label="Line 2"
                              value={thisCFData.rma_addr.line_2 || ""}
                              fullWidth
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                            />
                            <TextField
                              label="Line 3"
                              value={thisCFData.rma_addr.line_3 || ""}
                              fullWidth
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                            />
                            <Box display="flex" gap={0.5} width="100%">
                              <TextField
                                label="City"
                                value={thisCFData.rma_addr.city}
                                fullWidth
                                slotProps={{
                                  input: {
                                    readOnly: true,
                                  },
                                }}
                              />
                              <TextField
                                label="State"
                                value={thisCFData.rma_addr.state || ""}
                                fullWidth
                                slotProps={{
                                  input: {
                                    readOnly: true,
                                  },
                                }}
                              />
                            </Box>
                            <Box display="flex" gap={0.5} width="100%">
                              <TextField
                                label="Zip"
                                value={thisCFData.rma_addr.zip || ""}
                                fullWidth
                                slotProps={{
                                  input: {
                                    readOnly: true,
                                  },
                                }}
                              />
                              <TextField
                                label="Country"
                                value={thisCFData.rma_addr.country || ""}
                                fullWidth
                                slotProps={{
                                  input: {
                                    readOnly: true,
                                  },
                                }}
                              />
                            </Box>
                          </>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 400,
                }}
              >
                <CircularProgress />
                <Typography variant="h4">Loading Data...</Typography>
              </Box>
            )}
          </>
        ) : formMode === "edit" ? (
          <>
            {!!thisCFData && thisCFData.approved ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 400,
                }}
              >
                <CircularProgress />
                <Typography variant="h4">
                  This CF has already been approved and can no longer be edited.
                  If something needs to be changed, contact IT to have them make
                  the changes.
                </Typography>
              </Box>
            ) : !!thisCFData &&
              !(thisCFLoading || thisCFFetching) &&
              dataReady ? (
              <Paper sx={{ p: 3 }}>
                <form onSubmit={handleSubmit(onSubmit, console.error)}>
                  <Stack spacing={3} sx={{ width: "100%" }}>
                    <Stack direction="column" spacing={1} width="100%">
                      <Typography variant="h3">Issue Details</Typography>
                      <Box display="flex" gap={1} width="100%">
                        <Stack direction="column" spacing={1} width="50%">
                          <Controller
                            name="cf_category"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <Autocomplete
                                {...restField}
                                fullWidth
                                value={value}
                                onChange={(event, newValue) => {
                                  onChange(newValue);
                                  setValue(
                                    "issue_summary",
                                    newValue?.value.summary_prefill || "",
                                  );
                                  setValue("cat_other", "");
                                }}
                                options={CFCategoryOptions}
                                getOptionLabel={(option) => option?.label || ""}
                                isOptionEqualToValue={(option, value) =>
                                  option.value.id === value?.value.id
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Issue Category"
                                    error={!!formErrors.cf_category}
                                    helperText={formErrors.cf_category?.message}
                                  />
                                )}
                              />
                            )}
                          />
                          <Controller
                            name="issue_summary"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                label="Issue Summary"
                                fullWidth
                                error={!!formErrors.issue_summary}
                                helperText={formErrors.issue_summary?.message}
                                slotProps={{
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="date_of_occurrence"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <DatePicker
                                label="Date of Event Occurence"
                                value={value}
                                onChange={(date) => onChange(date)}
                                slotProps={{
                                  textField: {
                                    error: !!formErrors.date_of_occurrence,
                                    fullWidth: true,
                                    helperText:
                                      formErrors.date_of_occurrence?.message,
                                    ...restField,
                                  },
                                }}
                              />
                            )}
                          />
                        </Stack>
                        <Stack direction="column" spacing={1} width="50%">
                          <Controller
                            name="cat_other"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                label="Category: Other - Details"
                                fullWidth
                                error={!!formErrors.cat_other}
                                helperText={formErrors.cat_other?.message}
                                slotProps={{
                                  htmlInput: {
                                    maxLength: 100,
                                  },
                                }}
                              />
                            )}
                          />
                          <Controller
                            name="nature_of_issue"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                rows={4}
                                multiline={true}
                                label="Nature of Issue"
                                fullWidth
                                error={!!formErrors.nature_of_issue}
                                helperText={
                                  formErrors.nature_of_issue?.message ||
                                  "Include the sequence of events leading to the issue as well as any error codes displayed."
                                }
                              />
                            )}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                    <Stack direction="column" spacing={1} width="100%">
                      <Typography variant="h3">
                        Customer/Contact Details
                      </Typography>
                      <Box display="flex" gap={1} width={"100%"}>
                        <Stack direction="column" spacing={1} width="50%">
                          {customerLoading || customerFetching ? (
                            <>
                              <CircularProgress />
                              <Typography variant="body1">
                                Loading Customers...
                              </Typography>
                            </>
                          ) : (
                            <Controller
                              name="customer"
                              control={control}
                              render={({
                                field: { onChange, value, ...restField },
                              }) => {
                                return (
                                  <Autocomplete
                                    {...restField}
                                    fullWidth
                                    value={value}
                                    onChange={(event, newValue) => {
                                      onChange(newValue);
                                      setSelectedCustId(
                                        newValue ? newValue.value.id : null,
                                      );
                                    }}
                                    options={custOpts}
                                    getOptionLabel={(option) =>
                                      option?.label || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.value.id === value?.value.id
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Customer"
                                        error={!!formErrors.customer}
                                        helperText={
                                          formErrors.customer?.message
                                        }
                                      />
                                    )}
                                  />
                                );
                              }}
                            />
                          )}
                          <Controller
                            name="reported_to"
                            control={control}
                            render={({
                              field: { onChange, value, ...restField },
                            }) => {
                              return (
                                <Autocomplete
                                  {...restField}
                                  fullWidth
                                  value={value}
                                  onChange={(event, newValue) => {
                                    onChange(newValue);
                                  }}
                                  options={repToOpts}
                                  getOptionLabel={(option) =>
                                    option?.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option.value.id === value?.value.id
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Reported To"
                                      error={!!formErrors.reported_to}
                                      helperText={
                                        formErrors.reported_to?.message
                                      }
                                    />
                                  )}
                                />
                              );
                            }}
                          />
                          <Controller
                            name="date_reported"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <DatePicker
                                label="Date Reported"
                                value={value}
                                onChange={(date) => onChange(date)}
                                slotProps={{
                                  textField: {
                                    error: !!formErrors.date_reported,
                                    fullWidth: true,
                                    helperText:
                                      formErrors.date_reported?.message,
                                    ...restField,
                                  },
                                }}
                              />
                            )}
                          />
                        </Stack>
                        <Stack direction="column" spacing={1} width="50%">
                          <Controller
                            name="contact_name"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                label={
                                  anyReturning
                                    ? "RMA Contact Name"
                                    : "Contact Name"
                                }
                                fullWidth
                                error={!!formErrors.contact_name}
                                helperText={formErrors.contact_name?.message}
                              />
                            )}
                          />
                          <Controller
                            name="contact_email"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                label={
                                  anyReturning
                                    ? "RMA Contact Email"
                                    : "Contact Email"
                                }
                                fullWidth
                                error={!!formErrors.contact_email}
                                helperText={formErrors.contact_email?.message}
                              />
                            )}
                          />
                          <Controller
                            name="contact_phone"
                            control={control}
                            render={({
                              field: { value, onChange, ...restField },
                            }) => (
                              <TextField
                                value={value}
                                onChange={onChange}
                                {...restField}
                                label={
                                  anyReturning
                                    ? "RMA Contact Phone"
                                    : "Contact Phone"
                                }
                                fullWidth
                                error={!!formErrors.contact_phone}
                                helperText={formErrors.contact_phone?.message}
                              />
                            )}
                          />
                        </Stack>
                      </Box>
                    </Stack>
                    <Box display="flex" gap={1} width="100%">
                      <Stack direction="column" spacing={1} width="65%">
                        <Typography variant="h3">Product Details</Typography>
                        <ProductDataTable
                          tgcm={tgcmFieldArray}
                          tgdk={tgdkFieldArray}
                          tgct20={tgct20FieldArray}
                          tgct28={tgct28FieldArray}
                          tgps={tgpsFieldArray}
                          triggerDialogOpen={handleProdDialogOpen}
                          formMode={formMode}
                        />
                      </Stack>
                      <Stack direction="column" spacing={1} width="35%">
                        {anyReturning && (
                          <>
                            <Typography variant="h3">Return Address</Typography>
                            <Stack
                              direction="column"
                              width="100%"
                              justifyContent={"center"}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={haveAddr}
                                    onChange={(e, c) => {
                                      setHaveAddr(c);
                                    }}
                                  />
                                }
                                label="Have RMA Address?"
                                labelPlacement="top"
                              />
                              {!!formErrors.rma_addr?.message && (
                                <Box display="flex">
                                  <Typography variant="body1" color="error">
                                    {formErrors.rma_addr.message}
                                  </Typography>
                                </Box>
                              )}
                              {!!formErrors.rma_addr && !haveAddr && (
                                <Box display="flex">
                                  <Typography variant="body1" color="error">
                                    Address Errors preventing Form Submission,
                                    Toggle "Have RMA Address?" on to see them.
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                            {haveAddr && (
                              <>
                                {!selectedCustId ? (
                                  <Typography variant="body1">
                                    Select a Customer to get Address Options
                                  </Typography>
                                ) : addrFetching || addrLoading ? (
                                  <>
                                    <CircularProgress />
                                    <Typography variant="body1">
                                      Loading Customer Addresses...
                                    </Typography>
                                  </>
                                ) : (
                                  <Autocomplete
                                    fullWidth
                                    value={selectedAddr}
                                    onChange={(event, newValue) => {
                                      setSelectedAddr(newValue);
                                      if (newValue) {
                                        setValue(
                                          "rma_addr.attn",
                                          newValue.value.attn,
                                        );
                                        setValue(
                                          "rma_addr.addressee",
                                          newValue.value.addressee,
                                        );
                                        setValue(
                                          "rma_addr.line_1",
                                          newValue.value.line_1,
                                        );
                                        setValue(
                                          "rma_addr.line_2",
                                          newValue.value.line_2,
                                        );
                                        setValue(
                                          "rma_addr.line_3",
                                          newValue.value.line_3,
                                        );
                                        setValue(
                                          "rma_addr.city",
                                          newValue.value.city,
                                        );
                                        setValue(
                                          "rma_addr.state",
                                          newValue.value.state,
                                        );
                                        setValue(
                                          "rma_addr.country",
                                          newValue.value.country,
                                        );
                                        setValue(
                                          "rma_addr.zip",
                                          newValue.value.zip,
                                        );
                                      }
                                    }}
                                    options={allAddrOpts}
                                    getOptionLabel={(option) =>
                                      option?.label || ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      option.value.id === value?.value.id
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label="Address Quick-Fill"
                                        helperText={
                                          "Selecting an Address will overwrite anything in the Address Fields"
                                        }
                                      />
                                    )}
                                  />
                                )}
                                <Controller
                                  name="rma_addr.attn"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value || ""}
                                      onChange={onChange}
                                      {...restField}
                                      label="Attention"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.attn}
                                      helperText={
                                        formErrors.rma_addr?.attn?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.addressee"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value || ""}
                                      onChange={onChange}
                                      {...restField}
                                      label="Addressee"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.addressee}
                                      helperText={
                                        formErrors.rma_addr?.addressee?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.line_1"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value}
                                      onChange={onChange}
                                      {...restField}
                                      label="Line 1"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.line_1}
                                      helperText={
                                        formErrors.rma_addr?.line_1?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.line_2"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value || ""}
                                      onChange={onChange}
                                      {...restField}
                                      label="Line 2"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.line_2}
                                      helperText={
                                        formErrors.rma_addr?.line_2?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.line_3"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value || ""}
                                      onChange={onChange}
                                      {...restField}
                                      label="Line 3"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.line_3}
                                      helperText={
                                        formErrors.rma_addr?.line_3?.message
                                      }
                                    />
                                  )}
                                />
                                <Box display="flex" gap={0.5} width="100%">
                                  <Controller
                                    name="rma_addr.city"
                                    control={control}
                                    render={({
                                      field: { value, onChange, ...restField },
                                    }) => (
                                      <TextField
                                        value={value}
                                        onChange={onChange}
                                        {...restField}
                                        label="City"
                                        fullWidth
                                        error={!!formErrors.rma_addr?.city}
                                        helperText={
                                          formErrors.rma_addr?.city?.message
                                        }
                                      />
                                    )}
                                  />
                                  <Controller
                                    name="rma_addr.state"
                                    control={control}
                                    render={({
                                      field: { value, onChange, ...restField },
                                    }) => (
                                      <TextField
                                        value={value}
                                        onChange={onChange}
                                        {...restField}
                                        label="State"
                                        fullWidth
                                        error={!!formErrors.rma_addr?.state}
                                        helperText={
                                          formErrors.rma_addr?.state?.message
                                        }
                                      />
                                    )}
                                  />
                                </Box>
                                <Box display="flex" gap={0.5} width="100%">
                                  <Controller
                                    name="rma_addr.zip"
                                    control={control}
                                    render={({
                                      field: { value, onChange, ...restField },
                                    }) => (
                                      <TextField
                                        value={value}
                                        onChange={onChange}
                                        {...restField}
                                        label="Zip/Postal Code"
                                        fullWidth
                                        error={!!formErrors.rma_addr?.zip}
                                        helperText={
                                          formErrors.rma_addr?.zip?.message
                                        }
                                      />
                                    )}
                                  />
                                  <Controller
                                    name="rma_addr.country"
                                    control={control}
                                    render={({
                                      field: { value, onChange, ...restField },
                                    }) => (
                                      <TextField
                                        value={value}
                                        onChange={onChange}
                                        {...restField}
                                        label="Country"
                                        fullWidth
                                        error={!!formErrors.rma_addr?.country}
                                        helperText={
                                          formErrors.rma_addr?.country?.message
                                        }
                                      />
                                    )}
                                  />
                                </Box>
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    </Box>
                    <AddProductDialog
                      open={prodDialogOpen}
                      itemOpts={itemOpts}
                      allLots={allLots}
                      onCancel={handleProdDialogCancel}
                      onClose={handleProdDialogClose}
                      onSubmit={handleProdDialogSubmit}
                    />

                    {/* Form Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        justifyContent: "flex-end",
                        pt: 2,
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                        startIcon={<CancelIcon />}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={isSubmitting}
                      >
                        {isSubmitting
                          ? "Saving..."
                          : "Update Complaint Request"}
                      </Button>
                    </Box>
                  </Stack>
                </form>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 400,
                }}
              >
                <CircularProgress />
                <Typography variant="h4">Loading Data...</Typography>
              </Box>
            )}
          </>
        ) : formMode === "create" ? (
          <>
            {/* Form */}
            <Paper sx={{ p: 3 }}>
              <form onSubmit={handleSubmit(onSubmit, console.error)}>
                <Stack spacing={3} sx={{ width: "100%" }}>
                  <Stack direction="column" spacing={1} width="100%">
                    <Typography variant="h3">Issue Details</Typography>
                    <Box display="flex" gap={1} width="100%">
                      <Stack direction="column" spacing={1} width="50%">
                        <Controller
                          name="cf_category"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <Autocomplete
                              {...restField}
                              fullWidth
                              value={value}
                              onChange={(event, newValue) => {
                                onChange(newValue);
                                setValue(
                                  "issue_summary",
                                  newValue?.value.summary_prefill || "",
                                );
                                setValue("cat_other", "");
                              }}
                              options={CFCategoryOptions}
                              getOptionLabel={(option) => option?.label || ""}
                              isOptionEqualToValue={(option, value) =>
                                option.value.id === value?.value.id
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Issue Category"
                                  error={!!formErrors.cf_category}
                                  helperText={formErrors.cf_category?.message}
                                />
                              )}
                            />
                          )}
                        />
                        <Controller
                          name="issue_summary"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              label="Issue Summary"
                              fullWidth
                              error={!!formErrors.issue_summary}
                              helperText={formErrors.issue_summary?.message}
                              slotProps={{
                                htmlInput: {
                                  maxLength: 100,
                                },
                              }}
                            />
                          )}
                        />
                        <Controller
                          name="date_of_occurrence"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <DatePicker
                              label="Date of Event Occurence"
                              value={value}
                              onChange={(date) => onChange(date)}
                              slotProps={{
                                textField: {
                                  error: !!formErrors.date_of_occurrence,
                                  fullWidth: true,
                                  helperText:
                                    formErrors.date_of_occurrence?.message,
                                  ...restField,
                                },
                              }}
                            />
                          )}
                        />
                      </Stack>
                      <Stack direction="column" spacing={1} width="50%">
                        <Controller
                          name="cat_other"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              label="Category: Other - Details"
                              fullWidth
                              error={!!formErrors.cat_other}
                              helperText={formErrors.cat_other?.message}
                              slotProps={{
                                htmlInput: {
                                  maxLength: 100,
                                },
                              }}
                            />
                          )}
                        />
                        <Controller
                          name="nature_of_issue"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              rows={4}
                              multiline={true}
                              label="Nature of Issue"
                              fullWidth
                              error={!!formErrors.nature_of_issue}
                              helperText={
                                formErrors.nature_of_issue?.message ||
                                "Include the sequence of events leading to the issue as well as any error codes displayed."
                              }
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="column" spacing={1} width="100%">
                    <Typography variant="h3">
                      Customer/Contact Details
                    </Typography>
                    <Box display="flex" gap={1} width={"100%"}>
                      <Stack direction="column" spacing={1} width="50%">
                        {customerLoading || customerFetching ? (
                          <>
                            <CircularProgress />
                            <Typography variant="body1">
                              Loading Customers...
                            </Typography>
                          </>
                        ) : (
                          <Controller
                            name="customer"
                            control={control}
                            render={({
                              field: { onChange, value, ...restField },
                            }) => {
                              return (
                                <Autocomplete
                                  {...restField}
                                  fullWidth
                                  value={value}
                                  onChange={(event, newValue) => {
                                    onChange(newValue);
                                    setSelectedCustId(
                                      newValue ? newValue.value.id : null,
                                    );
                                  }}
                                  options={custOpts}
                                  getOptionLabel={(option) =>
                                    option?.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option.value.id === value?.value.id
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Customer"
                                      error={!!formErrors.customer}
                                      helperText={formErrors.customer?.message}
                                    />
                                  )}
                                />
                              );
                            }}
                          />
                        )}
                        <Controller
                          name="reported_to"
                          control={control}
                          render={({
                            field: { onChange, value, ...restField },
                          }) => {
                            return (
                              <Autocomplete
                                {...restField}
                                fullWidth
                                value={value}
                                onChange={(event, newValue) => {
                                  onChange(newValue);
                                }}
                                options={repToOpts}
                                getOptionLabel={(option) => option?.label || ""}
                                isOptionEqualToValue={(option, value) =>
                                  option.value.id === value?.value.id
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Reported To"
                                    error={!!formErrors.reported_to}
                                    helperText={formErrors.reported_to?.message}
                                  />
                                )}
                              />
                            );
                          }}
                        />
                        <Controller
                          name="date_reported"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <DatePicker
                              label="Date Reported"
                              value={value}
                              onChange={(date) => onChange(date)}
                              slotProps={{
                                textField: {
                                  error: !!formErrors.date_reported,
                                  fullWidth: true,
                                  helperText: formErrors.date_reported?.message,
                                  ...restField,
                                },
                              }}
                            />
                          )}
                        />
                      </Stack>
                      <Stack direction="column" spacing={1} width="50%">
                        <Controller
                          name="contact_name"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              label={
                                anyReturning
                                  ? "RMA Contact Name"
                                  : "Contact Name"
                              }
                              fullWidth
                              error={!!formErrors.contact_name}
                              helperText={formErrors.contact_name?.message}
                            />
                          )}
                        />
                        <Controller
                          name="contact_email"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              label={
                                anyReturning
                                  ? "RMA Contact Email"
                                  : "Contact Email"
                              }
                              fullWidth
                              error={!!formErrors.contact_email}
                              helperText={formErrors.contact_email?.message}
                            />
                          )}
                        />
                        <Controller
                          name="contact_phone"
                          control={control}
                          render={({
                            field: { value, onChange, ...restField },
                          }) => (
                            <TextField
                              value={value}
                              onChange={onChange}
                              {...restField}
                              label={
                                anyReturning
                                  ? "RMA Contact Phone"
                                  : "Contact Phone"
                              }
                              fullWidth
                              error={!!formErrors.contact_phone}
                              helperText={formErrors.contact_phone?.message}
                            />
                          )}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                  <Box display="flex" gap={1} width="100%">
                    <Stack direction="column" spacing={1} width="65%">
                      <Typography variant="h3">Product Details</Typography>
                      <ProductDataTable
                        tgcm={tgcmFieldArray}
                        tgdk={tgdkFieldArray}
                        tgct20={tgct20FieldArray}
                        tgct28={tgct28FieldArray}
                        tgps={tgpsFieldArray}
                        triggerDialogOpen={handleProdDialogOpen}
                        formMode={formMode}
                      />
                    </Stack>
                    <Stack direction="column" spacing={1} width="35%">
                      {anyReturning && (
                        <>
                          <Typography variant="h3">Return Address</Typography>
                          <Stack
                            direction="column"
                            width="100%"
                            justifyContent={"center"}
                          >
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={haveAddr}
                                  onChange={(e, c) => {
                                    setHaveAddr(c);
                                  }}
                                />
                              }
                              label="Have RMA Address?"
                              labelPlacement="top"
                            />
                            {!!formErrors.rma_addr?.message && (
                              <Box display="flex">
                                <Typography variant="body1" color="error">
                                  {formErrors.rma_addr.message}
                                </Typography>
                              </Box>
                            )}
                            {!!formErrors.rma_addr && !haveAddr && (
                              <Box display="flex">
                                <Typography variant="body1" color="error">
                                  Address Errors preventing Form Submission,
                                  Toggle "Have RMA Address?" on to see them.
                                </Typography>
                              </Box>
                            )}
                          </Stack>
                          {haveAddr && (
                            <>
                              {!selectedCustId ? (
                                <Typography variant="body1">
                                  Select a Customer to get Address Options
                                </Typography>
                              ) : addrFetching || addrLoading ? (
                                <>
                                  <CircularProgress />
                                  <Typography variant="body1">
                                    Loading Customer Addresses...
                                  </Typography>
                                </>
                              ) : (
                                <Autocomplete
                                  fullWidth
                                  value={selectedAddr}
                                  onChange={(event, newValue) => {
                                    setSelectedAddr(newValue);
                                    if (newValue) {
                                      setValue(
                                        "rma_addr.attn",
                                        newValue.value.attn,
                                      );
                                      setValue(
                                        "rma_addr.addressee",
                                        newValue.value.addressee,
                                      );
                                      setValue(
                                        "rma_addr.line_1",
                                        newValue.value.line_1,
                                      );
                                      setValue(
                                        "rma_addr.line_2",
                                        newValue.value.line_2,
                                      );
                                      setValue(
                                        "rma_addr.line_3",
                                        newValue.value.line_3,
                                      );
                                      setValue(
                                        "rma_addr.city",
                                        newValue.value.city,
                                      );
                                      setValue(
                                        "rma_addr.state",
                                        newValue.value.state,
                                      );
                                      setValue(
                                        "rma_addr.country",
                                        newValue.value.country,
                                      );
                                      setValue(
                                        "rma_addr.zip",
                                        newValue.value.zip,
                                      );
                                    }
                                  }}
                                  options={allAddrOpts}
                                  getOptionLabel={(option) =>
                                    option?.label || ""
                                  }
                                  isOptionEqualToValue={(option, value) =>
                                    option.value.id === value?.value.id
                                  }
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Address Quick-Fill"
                                      helperText={
                                        "Selecting an Address will overwrite anything in the Address Fields"
                                      }
                                    />
                                  )}
                                />
                              )}
                              <Controller
                                name="rma_addr.attn"
                                control={control}
                                render={({
                                  field: { value, onChange, ...restField },
                                }) => (
                                  <TextField
                                    value={value || ""}
                                    onChange={onChange}
                                    {...restField}
                                    label="Attention"
                                    fullWidth
                                    error={!!formErrors.rma_addr?.attn}
                                    helperText={
                                      formErrors.rma_addr?.attn?.message
                                    }
                                  />
                                )}
                              />
                              <Controller
                                name="rma_addr.addressee"
                                control={control}
                                render={({
                                  field: { value, onChange, ...restField },
                                }) => (
                                  <TextField
                                    value={value || ""}
                                    onChange={onChange}
                                    {...restField}
                                    label="Addressee"
                                    fullWidth
                                    error={!!formErrors.rma_addr?.addressee}
                                    helperText={
                                      formErrors.rma_addr?.addressee?.message
                                    }
                                  />
                                )}
                              />
                              <Controller
                                name="rma_addr.line_1"
                                control={control}
                                render={({
                                  field: { value, onChange, ...restField },
                                }) => (
                                  <TextField
                                    value={value}
                                    onChange={onChange}
                                    {...restField}
                                    label="Line 1"
                                    fullWidth
                                    error={!!formErrors.rma_addr?.line_1}
                                    helperText={
                                      formErrors.rma_addr?.line_1?.message
                                    }
                                  />
                                )}
                              />
                              <Controller
                                name="rma_addr.line_2"
                                control={control}
                                render={({
                                  field: { value, onChange, ...restField },
                                }) => (
                                  <TextField
                                    value={value || ""}
                                    onChange={onChange}
                                    {...restField}
                                    label="Line 2"
                                    fullWidth
                                    error={!!formErrors.rma_addr?.line_2}
                                    helperText={
                                      formErrors.rma_addr?.line_2?.message
                                    }
                                  />
                                )}
                              />
                              <Controller
                                name="rma_addr.line_3"
                                control={control}
                                render={({
                                  field: { value, onChange, ...restField },
                                }) => (
                                  <TextField
                                    value={value || ""}
                                    onChange={onChange}
                                    {...restField}
                                    label="Line 3"
                                    fullWidth
                                    error={!!formErrors.rma_addr?.line_3}
                                    helperText={
                                      formErrors.rma_addr?.line_3?.message
                                    }
                                  />
                                )}
                              />
                              <Box display="flex" gap={0.5} width="100%">
                                <Controller
                                  name="rma_addr.city"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value}
                                      onChange={onChange}
                                      {...restField}
                                      label="City"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.city}
                                      helperText={
                                        formErrors.rma_addr?.city?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.state"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value}
                                      onChange={onChange}
                                      {...restField}
                                      label="State"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.state}
                                      helperText={
                                        formErrors.rma_addr?.state?.message
                                      }
                                    />
                                  )}
                                />
                              </Box>
                              <Box display="flex" gap={0.5} width="100%">
                                <Controller
                                  name="rma_addr.zip"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value}
                                      onChange={onChange}
                                      {...restField}
                                      label="Zip/Postal Code"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.zip}
                                      helperText={
                                        formErrors.rma_addr?.zip?.message
                                      }
                                    />
                                  )}
                                />
                                <Controller
                                  name="rma_addr.country"
                                  control={control}
                                  render={({
                                    field: { value, onChange, ...restField },
                                  }) => (
                                    <TextField
                                      value={value}
                                      onChange={onChange}
                                      {...restField}
                                      label="Country"
                                      fullWidth
                                      error={!!formErrors.rma_addr?.country}
                                      helperText={
                                        formErrors.rma_addr?.country?.message
                                      }
                                    />
                                  )}
                                />
                              </Box>
                            </>
                          )}
                        </>
                      )}
                    </Stack>
                  </Box>
                  <AddProductDialog
                    open={prodDialogOpen}
                    itemOpts={itemOpts}
                    allLots={allLots}
                    onCancel={handleProdDialogCancel}
                    onClose={handleProdDialogClose}
                    onSubmit={handleProdDialogSubmit}
                  />

                  {/* Form Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "flex-end",
                      pt: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isSubmitting}
                      startIcon={<CancelIcon />}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<SaveIcon />}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Create Complaint Request"}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Paper>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <Typography variant="h1">
              {"You Aren't Supposed to Be Here. >:("}
            </Typography>
          </Box>
        )}
        <Dialog
          open={displayProcessDialog}
          onClose={(e, r) => {
            handleProcessDialogClose(e, r);
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            <Stack direction="column" gap={3}>
              {!!processDialogError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {processDialogError}
                </Alert>
              )}
              {formMode === "create" ? (
                <>
                  <Box display="flex" gap={2} justifyContent="space-evenly">
                    <Typography variant="h5">Nucleus</Typography>
                    {createCFMutation.isPending ? (
                      <CircularProgress />
                    ) : createCFMutation.isSuccess ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </Box>
                  {createCFMutation.isPending ? (
                    <Typography variant="h5">
                      Please wait while requests are completed...
                    </Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleProcessDialogClose(
                          null,
                          "escapeKeyDown",
                          !!createCFMutation.data ? "view" : undefined,
                          createCFMutation.data?.id,
                        );
                      }}
                    >
                      Close
                    </Button>
                  )}
                </>
              ) : formMode === "view" ? (
                <>
                  <Box display="flex" gap={2} justifyContent="space-evenly">
                    <Typography variant="h5">Approval</Typography>
                    {approveCFMutation.isPending ? (
                      <CircularProgress />
                    ) : approveCFMutation.data?.success ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </Box>
                  <Box display="flex" gap={2} justifyContent="space-evenly">
                    <Typography variant="h5">Post Processing</Typography>
                    {approveCFMutation.isPending ||
                    postProcCFMutation.isPending ? (
                      <CircularProgress />
                    ) : postProcCFMutation.data?.success ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </Box>
                  {approveCFMutation.isPending ||
                  postProcCFMutation.isPending ? (
                    <Typography variant="h5">
                      Please wait while requests are completed...
                    </Typography>
                  ) : (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        handleProcessDialogClose(
                          null,
                          "escapeKeyDown",
                          approveCFMutation.data?.success &&
                            postProcCFMutation.data?.success
                            ? "view"
                            : undefined,
                          id || undefined,
                        );
                      }}
                    >
                      Close
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Box display="flex" gap={2} justifyContent="space-evenly">
                    <Typography variant="h5">Updating</Typography>
                    {updateCFMutation.isPending ? (
                      <CircularProgress />
                    ) : updateCFMutation.data?.ok ? (
                      <CheckIcon color="primary" />
                    ) : (
                      <CancelIcon color="error" />
                    )}
                  </Box>
                  {updateCFMutation.isPending ? (
                    <Typography variant="h5">
                      Please wait while requests are completed...
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="h5">Requests completed.</Typography>
                      <Button
                        variant="outlined"
                        color={updateCFMutation.data?.ok ? "success" : "error"}
                        onClick={() =>
                          handleProcessDialogClose(
                            null,
                            "escapeKeyDown",
                            updateCFMutation.data?.ok ? "view" : undefined,
                            updateCFMutation.data?.ok
                              ? (updateCFMutation.data.id as string)
                              : undefined,
                          )
                        }
                      >
                        Close
                      </Button>
                    </>
                  )}
                </>
              )}
            </Stack>
          </DialogContent>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};
